/**
 * Copyright © 2025 Delicious AI, LLC
 *
 */

import { useQuery } from '@apollo/client'
import { Box, Button, Typography } from '@mui/material'
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import {
  GET_TENANT_STORES,
  GetTenantStoresQuery,
  GetTenantStoresQueryVariables,
} from '@/graphql'
import useAuth from '@/hooks/useAuth'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

import { useAccountSetUp } from '../account-set-up/use-account-set-up'

const { VITE_APP__ENVIRONMENT } = import.meta.env

function AuthGuard({ children }: { children?: React.ReactNode }) {
  const { t } = useT()
  const location = useLocation()
  const auth = useAuth()
  const loggedInUser = useLoggedInUser()
  const tenantStores = useQuery<
    GetTenantStoresQuery,
    GetTenantStoresQueryVariables
  >(GET_TENANT_STORES, {
    variables: {
      pagination: {
        offset: 0,
        limit: 10,
      },
    },
  })
  const { complete, loading, activeSubscription } = useAccountSetUp({
    auth,
    loggedInUser,
    tenantStores,
  })

  if (!auth.isInitialized || loading) {
    return null
  }

  if (loggedInUser.error?.networkError && auth.isAuthenticated) {
    if (VITE_APP__ENVIRONMENT === 'qa') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
          }}
        >
          <Typography variant="h6">
            {t('authGuard.serverError.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('authGuard.serverError.description')}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            {t('authGuard.serverError.refresh')}
          </Button>
        </Box>
      )
    }
    return null
  }

  if (!auth.isAuthenticated) {
    return (
      <Navigate
        to="/auth/sign-in"
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  if (
    (!activeSubscription || !complete) &&
    location.pathname !== '/account-set-up'
  ) {
    return (
      <Navigate
        to="/account-set-up"
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  return <>{children}</>
}

export default AuthGuard
